import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Bell, Plus, Trash2, Edit2, Eye, EyeOff, Clock, 
  Image as ImageIcon, Save, X, AlertCircle, Loader2,
  Check, XCircle, User
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = (process.env.REACT_APP_BACKEND_URL || window.location.origin);

const PopupNotificationsManagement = () => {
  const { user, isAdmin } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [pendingNotifications, setPendingNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [durationStatus, setDurationStatus] = useState({ total_allowed: 60, total_used: 0, remaining: 60 });
  const [showModal, setShowModal] = useState(false);
  const [editingNotification, setEditingNotification] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // 'all' or 'pending'
  const [language] = useState(localStorage.getItem('language') || 'ar');
  
  // Get token directly from localStorage for reliability
  const getToken = () => localStorage.getItem('token');
  
  const [form, setForm] = useState({
    title_ar: '',
    title_en: '',
    description_ar: '',
    description_en: '',
    image_url: '',
    duration_seconds: 5,
    display_order: 1
  });

  useEffect(() => {
    fetchNotifications();
    if (isAdmin) {
      fetchDurationStatus();
      fetchPendingNotifications();
    }
  }, [isAdmin]);

  const fetchNotifications = async () => {
    try {
      const res = await fetch(`${API_URL}/api/popup-notifications`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingNotifications = async () => {
    try {
      const res = await fetch(`${API_URL}/api/popup-notifications/pending`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPendingNotifications(data);
      }
    } catch (error) {
      console.error('Error fetching pending notifications:', error);
    }
  };

  const fetchDurationStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/popup-notifications/duration-status`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDurationStatus(data);
      }
    } catch (error) {
      console.error('Error fetching duration status:', error);
    }
  };

  const handleSubmit = async () => {
    if (!form.title_ar || !form.title_en || !form.description_ar || !form.description_en) {
      toast.error(language === 'ar' ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill all required fields');
      return;
    }

    setSaving(true);
    try {
      const url = editingNotification 
        ? `${API_URL}/api/popup-notifications/${editingNotification.id}`
        : `${API_URL}/api/popup-notifications`;
      
      const method = editingNotification ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(form)
      });

      const data = await res.json();
      
      if (res.ok) {
        toast.success(data.message || (language === 'ar' ? 'تم الحفظ بنجاح' : 'Saved successfully'));
        setShowModal(false);
        resetForm();
        fetchNotifications();
        fetchDurationStatus();
      } else {
        toast.error(data.detail || (language === 'ar' ? 'حدث خطأ' : 'An error occurred'));
      }
    } catch (error) {
      toast.error(language === 'ar' ? 'حدث خطأ في الاتصال' : 'Connection error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (notificationId) => {
    try {
      const res = await fetch(`${API_URL}/api/popup-notifications/${notificationId}/toggle`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });

      const data = await res.json();
      
      if (res.ok) {
        toast.success(data.message);
        fetchNotifications();
        fetchDurationStatus();
      } else {
        toast.error(data.detail);
      }
    } catch (error) {
      toast.error(language === 'ar' ? 'حدث خطأ' : 'An error occurred');
    }
  };

  const handleApprove = async (notificationId) => {
    try {
      const res = await fetch(`${API_URL}/api/popup-notifications/${notificationId}/approve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });

      const data = await res.json();
      
      if (res.ok) {
        toast.success(data.message);
        fetchNotifications();
        fetchPendingNotifications();
        fetchDurationStatus();
      } else {
        toast.error(data.detail);
      }
    } catch (error) {
      toast.error(language === 'ar' ? 'حدث خطأ' : 'An error occurred');
    }
  };

  const handleReject = async (notificationId) => {
    if (!window.confirm(language === 'ar' ? 'هل أنت متأكد من رفض هذا الإشعار؟' : 'Are you sure you want to reject this notification?')) {
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/popup-notifications/${notificationId}/reject`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });

      const data = await res.json();
      
      if (res.ok) {
        toast.success(data.message);
        fetchNotifications();
        fetchPendingNotifications();
      } else {
        toast.error(data.detail);
      }
    } catch (error) {
      toast.error(language === 'ar' ? 'حدث خطأ' : 'An error occurred');
    }
  };

  const handleDelete = async (notificationId) => {
    if (!window.confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذا الإشعار؟' : 'Are you sure you want to delete this notification?')) {
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/popup-notifications/${notificationId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });

      if (res.ok) {
        toast.success(language === 'ar' ? 'تم الحذف بنجاح' : 'Deleted successfully');
        fetchNotifications();
        fetchDurationStatus();
      }
    } catch (error) {
      toast.error(language === 'ar' ? 'حدث خطأ' : 'An error occurred');
    }
  };

  const handleEdit = (notification) => {
    setEditingNotification(notification);
    setForm({
      title_ar: notification.title_ar || '',
      title_en: notification.title_en || '',
      description_ar: notification.description_ar || '',
      description_en: notification.description_en || '',
      image_url: notification.image_url || '',
      duration_seconds: notification.duration_seconds || 5,
      display_order: notification.display_order || 1
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setForm({
      title_ar: '',
      title_en: '',
      description_ar: '',
      description_en: '',
      image_url: '',
      duration_seconds: 5,
      display_order: notifications.length + 1
    });
    setEditingNotification(null);
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="popup-notifications-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Bell className="w-6 h-6 text-primary" />
            {language === 'ar' ? 'الإشعارات المنبثقة' : 'Popup Notifications'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {language === 'ar' 
              ? (isAdmin ? 'إدارة الإشعارات التي تظهر للزوار في الصفحة الرئيسية' : 'اقترح إشعارات للظهور على الصفحة الرئيسية')
              : (isAdmin ? 'Manage notifications that appear to visitors on the homepage' : 'Suggest notifications for the homepage')}
          </p>
        </div>
        
        <Button onClick={openAddModal} className="bg-primary" data-testid="add-notification-btn">
          <Plus className="w-4 h-4 mr-2" />
          {language === 'ar' ? 'إضافة إشعار' : 'Add Notification'}
        </Button>
      </div>

      {/* Tabs for Admin */}
      {isAdmin && (
        <div className="flex gap-2">
          <Button
            variant={activeTab === 'all' ? 'default' : 'outline'}
            onClick={() => setActiveTab('all')}
            className={activeTab === 'all' ? 'bg-primary' : ''}
          >
            {language === 'ar' ? 'كل الإشعارات' : 'All Notifications'}
          </Button>
          <Button
            variant={activeTab === 'pending' ? 'default' : 'outline'}
            onClick={() => setActiveTab('pending')}
            className={activeTab === 'pending' ? 'bg-yellow-500' : ''}
          >
            {language === 'ar' ? 'بانتظار الاعتماد' : 'Pending Approval'}
            {pendingNotifications.length > 0 && (
              <span className="ml-2 bg-yellow-600 text-white text-xs px-2 py-0.5 rounded-full">
                {pendingNotifications.length}
              </span>
            )}
          </Button>
        </div>
      )}

      {/* Duration Status Card - Admin Only */}
      {isAdmin && (
        <Card className="bg-card border-white/10 p-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-primary" />
              <span className="text-white font-medium">
                {language === 'ar' ? 'المدة الإجمالية المستخدمة:' : 'Total Duration Used:'}
              </span>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-32 h-3 bg-zinc-700 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all ${
                      durationStatus.remaining === 0 ? 'bg-red-500' : 
                      durationStatus.remaining <= 15 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${(durationStatus.total_used / durationStatus.total_allowed) * 100}%` }}
                  />
                </div>
                <span className="text-white font-bold">
                  {durationStatus.total_used}/{durationStatus.total_allowed}
                  <span className="text-muted-foreground text-sm mr-1">
                    {language === 'ar' ? ' ثانية' : 's'}
                  </span>
                </span>
              </div>
              <span className={`text-sm px-2 py-1 rounded ${
                durationStatus.remaining === 0 ? 'bg-red-500/20 text-red-400' :
                durationStatus.remaining <= 15 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-green-500/20 text-green-400'
              }`}>
                {language === 'ar' ? `متبقي: ${durationStatus.remaining} ث` : `Remaining: ${durationStatus.remaining}s`}
              </span>
            </div>
          </div>
        </Card>
      )}

      {/* Notifications List */}
      {(activeTab === 'all' ? notifications : pendingNotifications).length === 0 ? (
        <Card className="bg-card border-white/10 p-8 text-center">
          <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">
            {activeTab === 'pending' 
              ? (language === 'ar' ? 'لا توجد إشعارات بانتظار الاعتماد' : 'No pending notifications')
              : (language === 'ar' ? 'لا توجد إشعارات منبثقة بعد' : 'No popup notifications yet')}
          </p>
        </Card>
      ) : (
        <div className="grid gap-4">
          {(activeTab === 'all' ? notifications : pendingNotifications).map((notification, index) => (
            <Card 
              key={notification.id} 
              className={`bg-card border-white/10 p-4 ${notification.status === 'rejected' ? 'opacity-50' : ''}`}
              data-testid={`notification-card-${index}`}
            >
              <div className="flex flex-col md:flex-row gap-4">
                {/* Image Preview */}
                {notification.image_url && (
                  <div className="w-full md:w-32 h-24 rounded-lg overflow-hidden bg-zinc-800 flex-shrink-0">
                    <img 
                      src={notification.image_url} 
                      alt={notification.title_en}
                      className="w-full h-full object-cover"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
                
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <h3 className="font-bold text-white text-lg">
                        {language === 'ar' ? notification.title_ar : notification.title_en}
                      </h3>
                      <p className="text-muted-foreground text-sm line-clamp-2">
                        {language === 'ar' ? notification.description_ar : notification.description_en}
                      </p>
                      {/* Created By */}
                      {notification.created_by_name && (
                        <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {language === 'ar' ? 'بواسطة:' : 'By:'} {notification.created_by_name}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* Status Badge */}
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        notification.status === 'approved' && notification.is_active
                          ? 'bg-green-500/20 text-green-400'
                          : notification.status === 'approved' && !notification.is_active
                          ? 'bg-zinc-500/20 text-zinc-400'
                          : notification.status === 'pending'
                          ? 'bg-yellow-500/20 text-yellow-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        {notification.status === 'approved' && notification.is_active
                          ? (language === 'ar' ? 'مفعّل' : 'Active')
                          : notification.status === 'approved' && !notification.is_active
                          ? (language === 'ar' ? 'معتمد/معطّل' : 'Approved/Inactive')
                          : notification.status === 'pending'
                          ? (language === 'ar' ? 'بانتظار الاعتماد' : 'Pending')
                          : (language === 'ar' ? 'مرفوض' : 'Rejected')}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {notification.duration_seconds} {language === 'ar' ? 'ثانية' : 'seconds'}
                    </span>
                    <span>
                      {language === 'ar' ? `الترتيب: ${notification.display_order}` : `Order: ${notification.display_order}`}
                    </span>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Approve/Reject buttons for pending notifications - Admin Only */}
                    {isAdmin && notification.status === 'pending' && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleApprove(notification.id)}
                          className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                          data-testid={`approve-btn-${index}`}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          {language === 'ar' ? 'اعتماد' : 'Approve'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleReject(notification.id)}
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                          data-testid={`reject-btn-${index}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          {language === 'ar' ? 'رفض' : 'Reject'}
                        </Button>
                      </>
                    )}
                    
                    {/* Toggle button for approved notifications - Admin Only */}
                    {isAdmin && notification.status === 'approved' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleToggle(notification.id)}
                        className={notification.is_active ? 'border-red-500/50 text-red-400' : 'border-green-500/50 text-green-400'}
                        data-testid={`toggle-btn-${index}`}
                      >
                        {notification.is_active ? (
                          <>
                            <EyeOff className="w-4 h-4 mr-1" />
                            {language === 'ar' ? 'تعطيل' : 'Deactivate'}
                          </>
                        ) : (
                          <>
                            <Eye className="w-4 h-4 mr-1" />
                            {language === 'ar' ? 'تفعيل' : 'Activate'}
                          </>
                        )}
                      </Button>
                    )}
                    
                    {/* Edit button - for own pending notifications (barber) or any (admin) */}
                    {(isAdmin || (notification.status === 'pending' && notification.created_by === user?.id)) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(notification)}
                        data-testid={`edit-btn-${index}`}
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        {language === 'ar' ? 'تعديل' : 'Edit'}
                      </Button>
                    )}
                    
                    {/* Delete button */}
                    {(isAdmin || (notification.status === 'pending' && notification.created_by === user?.id)) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(notification.id)}
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                        data-testid={`delete-btn-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-white/10 rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">
                {editingNotification 
                  ? (language === 'ar' ? 'تعديل الإشعار' : 'Edit Notification')
                  : (language === 'ar' ? 'إضافة إشعار جديد' : 'Add New Notification')}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-muted-foreground hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Arabic Title */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {language === 'ar' ? 'العنوان (عربي)' : 'Title (Arabic)'} *
                </label>
                <input
                  type="text"
                  value={form.title_ar}
                  onChange={(e) => setForm({ ...form, title_ar: e.target.value })}
                  className="w-full bg-zinc-800/50 border border-white/10 rounded-lg px-4 py-2 text-white"
                  placeholder="أدخل العنوان بالعربية"
                  dir="rtl"
                />
              </div>

              {/* English Title */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {language === 'ar' ? 'العنوان (إنجليزي)' : 'Title (English)'} *
                </label>
                <input
                  type="text"
                  value={form.title_en}
                  onChange={(e) => setForm({ ...form, title_en: e.target.value })}
                  className="w-full bg-zinc-800/50 border border-white/10 rounded-lg px-4 py-2 text-white"
                  placeholder="Enter title in English"
                />
              </div>

              {/* Arabic Description */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {language === 'ar' ? 'الوصف (عربي)' : 'Description (Arabic)'} *
                </label>
                <textarea
                  value={form.description_ar}
                  onChange={(e) => setForm({ ...form, description_ar: e.target.value })}
                  className="w-full bg-zinc-800/50 border border-white/10 rounded-lg px-4 py-2 text-white min-h-[80px]"
                  placeholder="أدخل الوصف بالعربية"
                  dir="rtl"
                />
              </div>

              {/* English Description */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {language === 'ar' ? 'الوصف (إنجليزي)' : 'Description (English)'} *
                </label>
                <textarea
                  value={form.description_en}
                  onChange={(e) => setForm({ ...form, description_en: e.target.value })}
                  className="w-full bg-zinc-800/50 border border-white/10 rounded-lg px-4 py-2 text-white min-h-[80px]"
                  placeholder="Enter description in English"
                />
              </div>

              {/* Image URL */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  <ImageIcon className="w-4 h-4 inline mr-1" />
                  {language === 'ar' ? 'رابط الصورة (اختياري)' : 'Image URL (Optional)'}
                </label>
                <input
                  type="url"
                  value={form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  className="w-full bg-zinc-800/50 border border-white/10 rounded-lg px-4 py-2 text-white"
                  placeholder="https://example.com/image.jpg"
                />
                {form.image_url && (
                  <div className="mt-2 w-full h-24 rounded-lg overflow-hidden bg-zinc-800">
                    <img 
                      src={form.image_url} 
                      alt="Preview"
                      className="w-full h-full object-cover"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>

              {/* Duration */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  <Clock className="w-4 h-4 inline mr-1" />
                  {language === 'ar' ? 'مدة العرض (ثانية)' : 'Display Duration (seconds)'} *
                </label>
                <input
                  type="number"
                  value={form.duration_seconds}
                  onChange={(e) => setForm({ ...form, duration_seconds: Math.min(60, Math.max(1, parseInt(e.target.value) || 1)) })}
                  className="w-full bg-zinc-800/50 border border-white/10 rounded-lg px-4 py-2 text-white"
                  min="1"
                  max="60"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {language === 'ar' 
                    ? `الحد الأقصى: ${durationStatus.remaining + (editingNotification?.duration_seconds || 0)} ثانية`
                    : `Maximum: ${durationStatus.remaining + (editingNotification?.duration_seconds || 0)} seconds`}
                </p>
              </div>

              {/* Display Order */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {language === 'ar' ? 'ترتيب العرض' : 'Display Order'}
                </label>
                <input
                  type="number"
                  value={form.display_order}
                  onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value) || 1 })}
                  className="w-full bg-zinc-800/50 border border-white/10 rounded-lg px-4 py-2 text-white"
                  min="1"
                  max="3"
                />
              </div>

              {/* Info for non-admin */}
              {!isAdmin && (
                <div className="flex items-center gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-yellow-400 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>
                    {language === 'ar' 
                      ? 'سيتم إرسال الإشعار للمدير للاعتماد قبل ظهوره للزوار'
                      : 'The notification will be sent to admin for approval before it appears to visitors'}
                  </span>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <Button onClick={handleSubmit} className="flex-1 bg-primary" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {language === 'ar' ? 'حفظ' : 'Save'}
              </Button>
              <Button onClick={() => setShowModal(false)} variant="outline" className="flex-1">
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PopupNotificationsManagement;
